import styled from '@emotion/styled'
import { Theme } from '@emotion/react'

export const ModalWrapper = styled.div`
  padding: 55px 47px 47px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  min-height: min(817px, calc(95vh - 50px));
  box-sizing: border-box;

  @media (max-width: 767px) {
    padding: 42px 16px 22px;
    gap: 18px;
    min-height: auto;
  }
`

export const ModalTitle = styled.div`
  font-size: 28px;
  line-height: 1.3;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.black};

  @media (max-width: 767px) {
    font-size: 24px;
  }
`

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 995px;
  max-width: 100%;

  @media (max-width: 767px) {
    gap: 8px;
  }
`

const inputStyles = ({ theme }: { theme: Theme }) => `
  width: 100%;
  border: 1px solid ${theme.colors.disable};
  border-radius: 25px;
  background: ${theme.colors.background.surface};
  color: ${theme.colors.text.black};
  font-size: 14px;
  line-height: 1.35;
  box-sizing: border-box;
  outline: none;
  padding: 10px 16px;

  &:focus {
    border-color: ${theme.colors.main};
    box-shadow: 0 0 0 2px ${theme.colors.main}33;
  }

  &.has-error {
    border-color: ${theme.colors.error};
  }

  &::placeholder {
    color: ${theme.colors.text.secondary};
  }
`

export const StyledTextarea = styled.textarea`
  ${inputStyles}
  resize: none;
  height: 80px;
`

export const TypeSelectWrapper = styled.div`
  width: 299px;
  max-width: 100%;

  @media (max-width: 767px) {
    width: 100%;
  }
`

export const FieldLabel = styled.label`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.black};
  text-transform: uppercase;
  letter-spacing: 0.1em;
`

export const AnswersSectionTitle = styled.div`
  font-size: 24px;
  line-height: 28px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.black};
  margin-top: 22px;
  margin-bottom: 28px;

  @media (max-width: 767px) {
    margin-top: 8px;
    margin-bottom: 16px;
    font-size: 22px;
    line-height: 26px;
  }
`

export const AnswersColumnsHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 28px;
  width: 988px;
  max-width: 100%;

  @media (max-width: 767px) {
    display: none;
  }
`

export const CorrectColHeader = styled.div`
  width: 40px;
  flex-shrink: 0;
  margin-right: 52px;
  font-size: 12px;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${({ theme }) => theme.colors.text.black};
`

export const AnswerColHeader = styled.div`
  flex: 1;
  font-size: 12px;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${({ theme }) => theme.colors.text.black};
`

export const AnswersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 990px;
  max-width: 100%;

  @media (max-width: 767px) {
    gap: 14px;
  }
`

export const AnswerRow = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 767px) {
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 10px;
    width: 100%;
  }
`

export const CorrectRadioWrapper = styled.div`
  width: 40px;
  flex-shrink: 0;
  margin-right: 52px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 767px) {
    margin-right: 0;
    width: 32px;
  }

  input[type='radio'] {
    appearance: none;
    -webkit-appearance: none;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 1px solid ${({ theme }) => theme.colors.text.secondary};
    background-color: ${({ theme }) => theme.colors.background.surface};
    cursor: pointer;
    margin: 0;
    flex-shrink: 0;
    transition: border-color 0.15s, background 0.15s;

    &:checked {
      border-color: ${({ theme }) => theme.colors.main};
      background-color: ${({ theme }) => theme.colors.main};
      box-shadow: inset 0 0 0 4px ${({ theme }) => theme.colors.background.surface};
    }

    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.colors.main};
      outline-offset: 2px;
    }
  }

  input[type='checkbox'] {
    appearance: none;
    -webkit-appearance: none;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 2px solid ${({ theme }) => theme.colors.disable};
    background-color: ${({ theme }) => theme.colors.background.surface};
    cursor: pointer;
    margin: 0;
    flex-shrink: 0;
    transition: border-color 0.15s, background-color 0.15s;

    &:checked {
      border-color: ${({ theme }) => theme.colors.main};
      background-color: ${({ theme }) => theme.colors.main};
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='white' d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: center;
      background-size: 16px;
    }

    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.colors.main};
      outline-offset: 2px;
    }
  }
`

export const AnswerInput = styled.input`
  ${inputStyles}
  flex: 0 1 780px;
  height: 40px;
  border-radius: 30px;

  @media (max-width: 767px) {
    flex: 1 1 calc(100% - 50px);
    min-width: 0;
  }
`

export const RemoveAnswerBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 0 12px;
  height: 40px;
  width: 100px;
  flex-shrink: 0;
  margin-left: 16px;
  border: 1px solid ${({ theme }) => theme.colors.main};
  background: transparent;
  border-radius: 30px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.black};
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.background.primaryAccent};
  }

  @media (max-width: 767px) {
    margin-left: 32px;
    width: auto;
    height: 34px;
  }
`

export const AddAnswerBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 12px;
  height: 40px;
  border: 1px solid ${({ theme }) => theme.colors.main};
  background: transparent;
  border-radius: 30px;
  color: ${({ theme }) => theme.colors.text.black};
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  width: fit-content;
  margin-top: 32px;

  &:hover {
    background: ${({ theme }) => theme.colors.background.primaryAccent};
  }

  @media (max-width: 767px) {
    margin-top: 20px;
    height: 34px;
  }
`

export const ButtonsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: auto;

  button {
    min-width: 242px;
    justify-content: center;
  }

  button:last-child {
    min-width: 320px;
  }

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 10px;
    margin-top: 8px;

    button {
      justify-content: center;
      width: 100%;
      min-width: 0;
      white-space: normal;
      text-align: center;
    }
  }
`

export const ErrorText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.error};
  line-height: 1.4;
`
